import { useMutation } from '@tanstack/react-query';
import React from 'react';

import { Beacon, Device, DeviceType, Gateway } from '@ixicare/resources';
import { UserAction } from '~config/notification';
import { t } from '~i18n';
import NotificationsStore from '~store/Notifications';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';

interface useDeviceArchiveProps {
  onClose?: () => void;
  onRefresh?: () => void;
  notifications: NotificationsStore;
}

export const useDeviceArchive = ({ onClose, onRefresh, notifications }: useDeviceArchiveProps) => {
  const [devicesToArchive, setDevicesToArchive] = React.useState<DBGWithType[]>([]);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (devicesToArchive.length === 0) throw new Error('No device selected');
      const results = await Promise.allSettled(devicesToArchive.map((d) => deleteDevice(d.type, Number(d.id))));
      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      return { failures };
    },
    onSuccess: ({ failures }) => {
      // A 412 means a device still has open alarms; surface that, but always
      // clear + refresh so the successfully-archived devices fall out of the list.
      if (failures.some((f) => f.reason?.status === 412)) {
        notifications.addUserActionNotification({
          type: UserAction.DeviceDelete,
          notificationType: 'error',
          message: t('device.delete.openAlertsError'),
        });
      }
      setDevicesToArchive([]);
      onClose?.();
      onRefresh?.();
    },
  });

  const deleteDevice = (type: DeviceType, deviceId: number) => {
    if (type === DeviceType.GATEWAY) return Gateway.deleteGateway(deviceId);
    if (type === DeviceType.BEACON) return Beacon.deleteBeacon(deviceId);
    return Device.deleteDevice(deviceId);
  };

  return {
    devicesToArchive,
    setDevicesToArchive,
    deleteMutation,
  };
};
